import { useToast } from "@/components/ui/use-toast";
import {
  OnboardingMessage,
  OnboardingMessageType,
  MessageContext,
  createOnboardingMessage,
  createOnboardingVideoMessage,
  deleteOnboardingMessage,
  getOnboardingMessages,
  reorderOnboardingMessages,
  updateOnboardingMessage,
} from "@/lib/api/onboarding-messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const QUERY_KEY = "onboarding-messages";

export const useMessages = (
  userId: string,
  messageContext: MessageContext = MessageContext.ONLINE,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, userId, messageContext],
    queryFn: () => getOnboardingMessages(userId, messageContext),
  });
};

export const useCreateMessage = (
  userId: string,
  messageContext: MessageContext = MessageContext.ONLINE,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      message,
      metadata,
    }: {
      message: string;
      metadata?: Record<string, unknown>;
    }) => createOnboardingMessage(userId, message, metadata, messageContext),
    onMutate: async ({ message, metadata }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });

      const previousMessages = queryClient.getQueryData<OnboardingMessage[]>([
        QUERY_KEY,
        userId,
        messageContext,
      ]);

      const optimisticMessage: OnboardingMessage = {
        id: crypto.randomUUID(),
        message,
        type: OnboardingMessageType.TEXT,
        message_context: messageContext,
        metadata: metadata || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        position: previousMessages ? previousMessages.length : 0,
      };

      queryClient.setQueryData<OnboardingMessage[]>(
        [QUERY_KEY, userId, messageContext],
        (old = []) => [...old, optimisticMessage],
      );

      return { previousMessages };
    },
    onError: (error: Error, _, context) => {
      queryClient.setQueryData(
        [QUERY_KEY, userId, messageContext],
        context?.previousMessages,
      );
      toast({
        title: error.message || "Failed to add message",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });
    },
  });
};

export const useCreateVideoMessage = (
  userId: string,
  messageContext: MessageContext = MessageContext.ONLINE,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      file,
      metadata,
    }: {
      file: File;
      metadata?: Record<string, unknown>;
    }) => createOnboardingVideoMessage(userId, file, metadata, messageContext),
    onSuccess: () => {
      toast({
        title: "Video message added successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to add video message",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });
    },
  });
};

export const useDeleteMessage = (
  userId: string,
  messageContext: MessageContext = MessageContext.ONLINE,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (messageId: string) => deleteOnboardingMessage(messageId),
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });

      const previousMessages = queryClient.getQueryData<OnboardingMessage[]>([
        QUERY_KEY,
        userId,
        messageContext,
      ]);

      queryClient.setQueryData<OnboardingMessage[]>(
        [QUERY_KEY, userId, messageContext],
        (old = []) => old.filter((message) => message.id !== messageId),
      );

      toast({
        title: "Message deleted successfully",
      });

      return { previousMessages };
    },
    onError: (error: Error, _, context) => {
      queryClient.setQueryData(
        [QUERY_KEY, userId, messageContext],
        context?.previousMessages,
      );
      toast({
        title: error.message || "Failed to delete message",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });
    },
  });
};

export const useReorderMessages = (
  userId: string,
  messageContext: MessageContext = MessageContext.ONLINE,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (messageIds: string[]) =>
      reorderOnboardingMessages(userId, messageIds, messageContext),
    onMutate: async (messageIds) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });

      const previousMessages = queryClient.getQueryData<OnboardingMessage[]>([
        QUERY_KEY,
        userId,
        messageContext,
      ]);

      if (!previousMessages) return { previousMessages };

      const messageMap = new Map(
        previousMessages.map((message) => [message.id, message]),
      );

      const reorderedMessages = messageIds
        .map((id, index) => {
          const message = messageMap.get(id);
          if (!message) return null;
          return { ...message, position: index };
        })
        .filter((msg): msg is OnboardingMessage => msg !== null);

      queryClient.setQueryData<OnboardingMessage[]>(
        [QUERY_KEY, userId, messageContext],
        reorderedMessages,
      );

      return { previousMessages };
    },
    onError: (error: Error, _, context) => {
      queryClient.setQueryData(
        [QUERY_KEY, userId, messageContext],
        context?.previousMessages,
      );
      toast({
        title: error.message || "Failed to reorder messages",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });
    },
  });
};

export const useUpdateMessage = (
  userId: string,
  messageContext: MessageContext = MessageContext.ONLINE,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      messageId,
      message,
      metadata,
    }: {
      messageId: string;
      message: string;
      metadata?: Record<string, unknown>;
    }) => updateOnboardingMessage(messageId, message, metadata, messageContext),
    onMutate: async ({ messageId, message, metadata }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });

      const previousMessages = queryClient.getQueryData<OnboardingMessage[]>([
        QUERY_KEY,
        userId,
        messageContext,
      ]);

      if (!previousMessages) return { previousMessages };

      const updatedMessages = previousMessages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              message,
              metadata: metadata || msg.metadata,
              updated_at: new Date().toISOString(),
            }
          : msg,
      );

      queryClient.setQueryData<OnboardingMessage[]>(
        [QUERY_KEY, userId, messageContext],
        updatedMessages,
      );

      return { previousMessages };
    },
    onError: (error: Error, _, context) => {
      queryClient.setQueryData(
        [QUERY_KEY, userId, messageContext],
        context?.previousMessages,
      );
      toast({
        title: error.message || "Failed to update message",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, userId, messageContext],
      });
    },
  });
};
